import type {
  ChatCall,
  ChatConversationSummary,
  ChatMessage,
  ChatMessagesResponse,
  ChatStreamReadEvent,
} from '@bits/shared/zod';
import { isImageContentType } from '@bits/domain';

// Pure helpers behind the chat screens: cache updates driven by stream
// events, thread layout, and composer parsing. No React, no IO.

export type MessagePage = ChatMessagesResponse;

const GROUP_WINDOW_MS = 5 * 60 * 1000;
const CLIENT_ID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export function compareIds(a: string, b: string): number {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function newClientMessageId(random: (n: number) => number[] = randomBytes): string {
  const time = Date.now().toString(32).toUpperCase().padStart(10, '0');
  const tail = random(16)
    .map((b) => CLIENT_ID_ALPHABET[b % 32])
    .join('');
  return (time + tail).slice(0, 26);
}

function randomBytes(n: number): number[] {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return [...out];
}

export function conversationTitle(c: ChatConversationSummary, meId: string | null): string {
  if (c.kind === 'group') return c.title ?? 'Group';
  const other = c.members.find((m) => m.personId !== meId);
  return other?.name || 'Direct message';
}

export function otherMemberIds(c: ChatConversationSummary, meId: string | null): string[] {
  return c.members.map((m) => m.personId).filter((id) => id !== meId);
}

export function totalUnread(list: readonly ChatConversationSummary[]): number {
  return list.reduce((sum, c) => sum + c.unreadCount, 0);
}

function activityAt(c: ChatConversationSummary): string {
  return c.lastMessage?.createdAt ?? c.createdAt;
}

export function sortByActivity(list: readonly ChatConversationSummary[]): ChatConversationSummary[] {
  return [...list].sort((a, b) => activityAt(b).localeCompare(activityAt(a)));
}

export function upsertConversation(
  list: readonly ChatConversationSummary[],
  conversation: ChatConversationSummary,
): ChatConversationSummary[] {
  const rest = list.filter((c) => c.id !== conversation.id);
  return sortByActivity([...rest, conversation]);
}

export function removeConversation(
  list: readonly ChatConversationSummary[],
  conversationId: string,
): ChatConversationSummary[] {
  return list.filter((c) => c.id !== conversationId);
}

export function hasConversation(
  list: readonly ChatConversationSummary[],
  conversationId: string,
): boolean {
  return list.some((c) => c.id === conversationId);
}

// Mirrors the server's unread rule: everything after my cursor that I did not
// write counts, system lines included. My own send moves my cursor.
export function applyMessageToList(
  list: readonly ChatConversationSummary[],
  message: ChatMessage,
  meId: string | null,
): ChatConversationSummary[] {
  return sortByActivity(
    list.map((c) => {
      if (c.id !== message.conversationId) return c;
      if (c.lastMessage && compareIds(c.lastMessage.id, message.id) >= 0) return c;
      const mine = message.authorId !== null && message.authorId === meId;
      return {
        ...c,
        lastMessage: message,
        unreadCount: mine ? c.unreadCount : c.unreadCount + 1,
        myLastReadMessageId: mine ? message.id : c.myLastReadMessageId,
        members: mine
          ? c.members.map((m) =>
              m.personId === meId ? { ...m, lastReadMessageId: message.id } : m,
            )
          : c.members,
      };
    }),
  );
}

export function applyReadToList(
  list: readonly ChatConversationSummary[],
  event: ChatStreamReadEvent,
  meId: string | null,
): ChatConversationSummary[] {
  return list.map((c) => {
    if (c.id !== event.conversationId) return c;
    const members = c.members.map((m) =>
      m.personId === event.personId &&
      (m.lastReadMessageId === null || compareIds(m.lastReadMessageId, event.upToMessageId) < 0)
        ? { ...m, lastReadMessageId: event.upToMessageId }
        : m,
    );
    if (event.personId !== meId) return { ...c, members };
    const caughtUp =
      c.lastMessage === null || compareIds(event.upToMessageId, c.lastMessage.id) >= 0;
    return {
      ...c,
      members,
      myLastReadMessageId: event.upToMessageId,
      unreadCount: caughtUp ? 0 : c.unreadCount,
    };
  });
}

export function applyCallToList(
  list: readonly ChatConversationSummary[],
  call: ChatCall,
): ChatConversationSummary[] {
  return list.map((c) => {
    if (c.id !== call.conversationId) return c;
    if (call.status === 'ended') {
      return c.activeCall?.id === call.id ? { ...c, activeCall: null } : c;
    }
    return { ...c, activeCall: call };
  });
}

export function joinedPeerIds(call: ChatCall, meId: string | null): string[] {
  return call.participants
    .filter((p) => p.status === 'joined' && p.personId !== meId)
    .map((p) => p.personId);
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function callEndedLabel(call: ChatCall, meId: string | null): string {
  const label = call.kind === 'video' ? 'Video call' : 'Audio call';
  switch (call.endReason) {
    case 'completed': {
      const seconds =
        call.answeredAt && call.endedAt
          ? (Date.parse(call.endedAt) - Date.parse(call.answeredAt)) / 1000
          : 0;
      return `${label} ended · ${formatDuration(seconds)}`;
    }
    case 'missed':
      return call.startedBy === meId ? 'No answer' : `Missed ${call.kind} call`;
    case 'declined':
      return `${label} declined`;
    case 'cancelled':
      return `${label} cancelled`;
    case 'failed':
      return `${label} failed`;
    default:
      return `${label} ended`;
  }
}

export function redactMessage(message: ChatMessage): ChatMessage {
  return { ...message, redacted: true, body: '' };
}

export function applyRedactionToList(
  list: readonly ChatConversationSummary[],
  conversationId: string,
  messageId: string,
): ChatConversationSummary[] {
  return list.map((c) =>
    c.id === conversationId && c.lastMessage?.id === messageId
      ? { ...c, lastMessage: redactMessage(c.lastMessage) }
      : c,
  );
}

export function appendMessage(page: MessagePage, message: ChatMessage): MessagePage {
  if (page.messages.some((m) => m.id === message.id)) return page;
  const messages = [...page.messages, message].sort((a, b) => compareIds(a.id, b.id));
  return { ...page, messages };
}

export function redactInPage(page: MessagePage, messageId: string): MessagePage {
  return {
    ...page,
    messages: page.messages.map((m) => (m.id === messageId ? redactMessage(m) : m)),
  };
}

// An edit or a reaction arrives as the whole message; a page that does not
// hold it (scrolled out, never loaded) is left alone rather than grown.
export function replaceInPage(page: MessagePage, message: ChatMessage): MessagePage {
  if (!page.messages.some((m) => m.id === message.id)) return page;
  return { ...page, messages: page.messages.map((m) => (m.id === message.id ? message : m)) };
}

export function applyUpdatedToList(
  list: readonly ChatConversationSummary[],
  message: ChatMessage,
): ChatConversationSummary[] {
  return list.map((c) =>
    c.id === message.conversationId && c.lastMessage?.id === message.id
      ? { ...c, lastMessage: message }
      : c,
  );
}

export function reactedByMe(reaction: { personIds: string[] }, meId: string | null): boolean {
  return meId !== null && reaction.personIds.includes(meId);
}

export function reactionTitle(
  reaction: { emoji: string; personIds: string[] },
  names: ReadonlyMap<string, string>,
  meId: string | null,
): string {
  const who = reaction.personIds.map((id) => (id === meId ? 'You' : (names.get(id) ?? 'Someone')));
  return `${reaction.emoji} ${who.join(', ')}`;
}

export function prependOlder(page: MessagePage, older: MessagePage): MessagePage {
  const seen = new Set(page.messages.map((m) => m.id));
  const fresh = older.messages.filter((m) => !seen.has(m.id));
  return {
    messages: [...fresh, ...page.messages].sort((a, b) => compareIds(a.id, b.id)),
    hasMore: older.hasMore,
  };
}

export type ThreadItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'message'; key: string; message: ChatMessage; firstInGroup: boolean; lastInGroup: boolean };

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function dayLabel(date: Date, now: Date): string {
  const today = localDayKey(now);
  const key = localDayKey(date);
  if (key === today) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === localDayKey(yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

function sameGroup(a: ChatMessage, b: ChatMessage): boolean {
  if (a.kind !== 'text' || b.kind !== 'text') return false;
  if (a.authorId !== b.authorId) return false;
  return Math.abs(Date.parse(b.createdAt) - Date.parse(a.createdAt)) <= GROUP_WINDOW_MS;
}

export function buildThreadItems(
  messages: readonly ChatMessage[],
  now: Date = new Date(),
): ThreadItem[] {
  const items: ThreadItem[] = [];
  let lastDay: string | null = null;
  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i]!;
    const date = new Date(message.createdAt);
    const day = localDayKey(date);
    if (day !== lastDay) {
      items.push({ kind: 'day', key: `day-${day}`, label: dayLabel(date, now) });
      lastDay = day;
    }
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const firstInGroup = !prev || !sameGroup(prev, message) || localDayKey(new Date(prev.createdAt)) !== day;
    const lastInGroup = !next || !sameGroup(message, next) || localDayKey(new Date(next.createdAt)) !== day;
    items.push({ kind: 'message', key: message.id, message, firstInGroup, lastInGroup });
  }
  return items;
}

export function seenByNames(
  c: ChatConversationSummary,
  messageId: string,
  meId: string | null,
): string[] {
  return c.members
    .filter(
      (m) =>
        m.personId !== meId &&
        m.lastReadMessageId !== null &&
        compareIds(m.lastReadMessageId, messageId) >= 0,
    )
    .map((m) => m.name);
}

export function lastOwnMessageId(messages: readonly ChatMessage[], meId: string | null): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]!;
    if (m.authorId === meId && m.kind === 'text') return m.id;
  }
  return null;
}

// An `@` that starts a word and runs up to the caret with no whitespace is a
// mention in progress; anything else (emails, mid-word @) is left alone.
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}

// Only people whose `@Name` is still in the text are sent as mentions, so a
// name typed then deleted does not notify anyone.
export function mentionsStillPresent(
  text: string,
  picked: ReadonlyMap<string, string>,
): string[] {
  return [...picked.entries()]
    .filter(([, name]) => text.includes(`@${name}`))
    .map(([personId]) => personId);
}

const URL_PATTERN = /https?:\/\/[^\s<>"']+/g;

export type BodySegment = { kind: 'text'; text: string } | { kind: 'link'; href: string };

export function segmentBody(body: string): BodySegment[] {
  const out: BodySegment[] = [];
  let last = 0;
  for (const match of body.matchAll(URL_PATTERN)) {
    const index = match.index ?? 0;
    let href = match[0];
    let trailing = '';
    while (/[.,;:!?)]$/.test(href)) {
      trailing = href.slice(-1) + trailing;
      href = href.slice(0, -1);
    }
    if (index > last) out.push({ kind: 'text', text: body.slice(last, index) });
    out.push({ kind: 'link', href });
    if (trailing) out.push({ kind: 'text', text: trailing });
    last = index + match[0].length;
  }
  if (last < body.length) out.push({ kind: 'text', text: body.slice(last) });
  return out;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return (first + second).toUpperCase() || '?';
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function formatListTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (localDayKey(date) === localDayKey(now)) return formatClock(iso);
  const dayMs = 24 * 60 * 60 * 1000;
  if (now.getTime() - date.getTime() < 7 * dayMs) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

export function isImageAttachment(m: ChatMessage): boolean {
  return m.attachment !== null && isImageContentType(m.attachment.contentType);
}

// One line that stands in for a message wherever its body cannot be shown:
// the list preview, a reply excerpt, a mention email.
export function attachmentLabel(m: ChatMessage): string | null {
  if (!m.attachment) return null;
  if (m.kind === 'voice') {
    return `🎤 Voice note · ${formatDuration((m.attachment.durationMs ?? 0) / 1000)}`;
  }
  return isImageContentType(m.attachment.contentType) ? '📷 Photo' : `📎 ${m.attachment.fileName}`;
}

export function messagePreview(
  c: ChatConversationSummary,
  meId: string | null,
): string {
  const m = c.lastMessage;
  if (!m) return c.kind === 'group' ? 'No messages yet' : 'Say hello';
  if (m.redacted) return 'Message unsent';
  if (m.kind === 'system') return m.body;
  const who = m.authorId === meId ? 'You' : (m.authorName ?? 'Someone');
  const text = m.body || attachmentLabel(m) || '';
  return c.kind === 'group' || m.authorId === meId ? `${who}: ${text}` : text;
}

const RECORDER_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

// Chromium and Firefox record WebM/Opus, Safari records MP4/AAC; the first
// type the browser admits to is the one the server stores.
export function pickRecorderType(isSupported: (type: string) => boolean): string | null {
  return RECORDER_TYPES.find((type) => isSupported(type)) ?? null;
}

export function voiceNoteFileName(mimeType: string, now: Date = new Date()): string {
  const base = mimeType.split(';')[0] ?? '';
  const ext = base === 'audio/mp4' ? 'm4a' : base === 'audio/ogg' ? 'ogg' : 'webm';
  return `Voice note ${stampFor(now)}.${ext}`;
}

export function pastedImageName(contentType: string, now: Date = new Date()): string {
  const ext = contentType === 'image/jpeg' ? 'jpg' : contentType === 'image/webp' ? 'webp' : contentType === 'image/gif' ? 'gif' : 'png';
  return `Pasted image ${stampFor(now)}.${ext}`;
}

function stampFor(now: Date): string {
  const two = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())} ${two(now.getHours())}-${two(now.getMinutes())}-${two(now.getSeconds())}`;
}
